/**
 * Workspace Store
 *
 * Manages the current workspace context for Mavriq team members.
 * Allows switching between client company views for data access and reporting.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api'

// Types
export interface WorkspaceContext {
  user_id: string
  company_id: string
  company_name: string
  is_global: boolean
  selected_at?: string
}

export interface WorkspaceCompany {
  id: string
  name: string
  industry: string
  tier: string
  status: string
  classification: string
  has_integrations: boolean
  ga_enabled: boolean
  ads_enabled: boolean
}

/**
 * Feature flags that can be enabled/disabled per client
 */
export interface FeatureFlags {
  crm: boolean // Contacts, Companies, Deals
  projects: boolean // Projects, Time Tracking
  invoicing: boolean // Invoices, Proposals
  social: boolean // Social media management
  sequences: boolean // Email sequences/campaigns
  lead_database: boolean // Lead search/enrichment
  analytics: boolean // Google Analytics dashboard
  ads: boolean // Ad platforms (Google, Meta, LinkedIn)
  reports: boolean // Reporting dashboards
  client_portal: boolean // Client self-service portal
  automations: boolean // Workflow automations
}

export interface CompanyIntegration {
  id: string
  company_id: string
  company_name?: string
  // Google Analytics
  ga_enabled: boolean
  ga_property_id: string
  ga_credentials_configured: boolean
  ga_last_sync_at: string | null
  ga_sync_error: string
  // Google Ads
  google_ads_enabled: boolean
  google_ads_customer_id: string
  google_ads_configured: boolean
  google_ads_last_sync_at: string | null
  google_ads_sync_error: string
  // Meta Ads
  meta_ads_enabled: boolean
  meta_ads_account_id: string
  meta_ads_configured: boolean
  meta_ads_last_sync_at: string | null
  meta_ads_sync_error: string
  // LinkedIn Ads
  linkedin_ads_enabled: boolean
  linkedin_ads_account_id: string
  linkedin_ads_configured: boolean
  linkedin_ads_last_sync_at: string | null
  linkedin_ads_sync_error: string
  // Enrichment
  enrichment_providers: Record<string, { enabled: boolean; configured: boolean }>
  // Email
  email_provider: string
  email_configured: boolean
  email_from_name: string
  email_from_email: string
  email_reply_to: string
  // Branding
  custom_domain: string
  portal_subdomain: string
  branding_logo_url: string
  branding_primary_color: string
  branding_secondary_color: string
  // Feature Flags
  features: FeatureFlags
  feature_crm_enabled: boolean
  feature_projects_enabled: boolean
  feature_invoicing_enabled: boolean
  feature_social_enabled: boolean
  feature_sequences_enabled: boolean
  feature_lead_database_enabled: boolean
  feature_analytics_enabled: boolean
  feature_ads_enabled: boolean
  feature_reports_enabled: boolean
  feature_client_portal_enabled: boolean
  feature_automations_enabled: boolean
  // External CRM info
  setup_notes: string
  external_crm: string
  external_crm_url: string
  // Vertical (drives vertical-specific navigation, e.g. 'coworking')
  vertical_type: string
  // Meta
  has_any_integration: boolean
  enabled_ad_platforms: string[]
}

export const useWorkspaceStore = defineStore('workspace', () => {
  // State
  const context = ref<WorkspaceContext | null>(null)
  const companies = ref<WorkspaceCompany[]>([])
  const currentIntegration = ref<CompanyIntegration | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isGlobalView = computed(() => !context.value?.company_id)
  const currentCompanyId = computed(() => context.value?.company_id || '')
  const currentCompanyName = computed(() => context.value?.company_name || 'All Companies')

  const currentCompany = computed(() => {
    if (!currentCompanyId.value) return null
    return companies.value.find(c => c.id === currentCompanyId.value) || null
  })

  /**
   * Get enabled features for current workspace.
   * In global view, all features are enabled.
   * In company view, returns the company's feature flags.
   */
  const enabledFeatures = computed<FeatureFlags>(() => {
    // In global view, all features are enabled
    if (isGlobalView.value || !currentIntegration.value?.features) {
      return {
        crm: true,
        projects: true,
        invoicing: true,
        social: true,
        sequences: true,
        lead_database: true,
        analytics: true,
        ads: true,
        reports: true,
        client_portal: true,
        automations: true,
      }
    }
    return currentIntegration.value.features
  })

  /**
   * Check if a specific feature is enabled in current workspace
   */
  function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return enabledFeatures.value[feature] ?? true
  }

  /**
   * External CRM info for current workspace (if they use HubSpot, etc.)
   */
  const externalCRM = computed(() => {
    if (!currentIntegration.value) return null
    if (!currentIntegration.value.external_crm) return null
    return {
      name: currentIntegration.value.external_crm,
      url: currentIntegration.value.external_crm_url,
      notes: currentIntegration.value.setup_notes,
    }
  })

  // Actions
  async function fetchContext() {
    try {
      const response = await apiClient.get<{ data: WorkspaceContext }>('/workspace/context')
      context.value = response.data.data
      return context.value
    } catch (err) {
      console.error('Failed to fetch workspace context:', err)
      // Default to global view
      context.value = {
        user_id: '',
        company_id: '',
        company_name: '',
        is_global: true,
      }
      return context.value
    }
  }

  async function fetchCompanies(classification?: string) {
    loading.value = true
    error.value = null
    try {
      const params: Record<string, string> = {}
      if (classification) params.classification = classification
      const response = await apiClient.get<{ data: { companies: WorkspaceCompany[] } }>(
        '/workspace/companies',
        { params }
      )
      companies.value = response.data.data.companies
      return companies.value
    } catch (err) {
      error.value = 'Failed to load companies'
      console.error('Failed to fetch workspace companies:', err)
      return []
    } finally {
      loading.value = false
    }
  }

  async function switchWorkspace(companyId: string) {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post<{ data: WorkspaceContext }>('/workspace/context', {
        company_id: companyId,
      })
      context.value = response.data.data

      // Save to localStorage for API interceptor to use
      if (companyId) {
        localStorage.setItem('workspace_company_id', companyId)
        await fetchCompanyIntegration(companyId)
      } else {
        localStorage.removeItem('workspace_company_id')
        localStorage.removeItem('workspace_vertical')
        currentIntegration.value = null
      }

      return context.value
    } catch (err) {
      error.value = 'Failed to switch workspace'
      console.error('Failed to switch workspace:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  async function switchToGlobal() {
    return switchWorkspace('')
  }

  async function fetchCompanyIntegration(companyId: string) {
    try {
      const response = await apiClient.get<{ data: CompanyIntegration }>(
        `/companies/${companyId}/integrations`
      )
      // Only the ACTIVE workspace's integration drives app chrome (rail,
      // vertical-gated views). Settings pages fetch other companies' configs
      // through here too — letting those overwrite it turned the whole app
      // into CO4's coworking rail while in "All clients".
      if (companyId === currentCompanyId.value) {
        currentIntegration.value = response.data.data
        // Persist the vertical so vertical-gated views (which fetch on mount)
        // know it synchronously on a page reload, before this async call resolves.
        try { localStorage.setItem('workspace_vertical', (currentIntegration.value as any)?.vertical_type || '') } catch { /* ignore */ }
      }
      return response.data.data
    } catch (err) {
      console.error('Failed to fetch company integration:', err)
      if (companyId === currentCompanyId.value) currentIntegration.value = null
      return null
    }
  }

  async function updateCompanyIntegration(
    companyId: string,
    data: Partial<CompanyIntegration>
  ): Promise<CompanyIntegration> {
    const response = await apiClient.put<{ data: CompanyIntegration }>(
      `/companies/${companyId}/integrations`,
      data
    )
    if (companyId === currentCompanyId.value) currentIntegration.value = response.data.data
    return response.data.data
  }

  async function testIntegration(
    companyId: string,
    platform: 'ga' | 'google_ads' | 'meta_ads' | 'linkedin_ads'
  ) {
    const response = await apiClient.post<{
      data: { platform: string; success: boolean; message: string }
    }>(`/companies/${companyId}/integrations/test/${platform}`)
    return response.data.data
  }

  async function syncIntegration(
    companyId: string,
    platform: 'ga' | 'google_ads' | 'meta_ads' | 'linkedin_ads'
  ) {
    const response = await apiClient.post<{
      data: { platform: string; success: boolean; message: string; records_synced: number }
    }>(`/companies/${companyId}/integrations/sync/${platform}`)
    return response.data.data
  }

  // Initialize
  async function initialize() {
    await Promise.all([fetchContext(), fetchCompanies()])
    if (context.value?.company_id) {
      // Sync localStorage with server context
      localStorage.setItem('workspace_company_id', context.value.company_id)
      await fetchCompanyIntegration(context.value.company_id)
    } else {
      localStorage.removeItem('workspace_company_id')
      localStorage.removeItem('workspace_vertical')
      currentIntegration.value = null
    }
  }

  function reset() {
    context.value = null
    companies.value = []
    currentIntegration.value = null
    error.value = null
  }

  return {
    // State
    context,
    companies,
    currentIntegration,
    loading,
    error,
    // Computed
    isGlobalView,
    currentCompanyId,
    currentCompanyName,
    currentCompany,
    enabledFeatures,
    externalCRM,
    // Actions
    fetchContext,
    fetchCompanies,
    switchWorkspace,
    switchToGlobal,
    fetchCompanyIntegration,
    updateCompanyIntegration,
    testIntegration,
    syncIntegration,
    initialize,
    reset,
    isFeatureEnabled,
  }
})
